
import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'

import {
  MedicationAmounts as Amounts,
  MedicationFrequencies as Frequencies,
  MedicationRoutes as Routes,
  MedicationUnits as Units
} from '../../../../lib/Codes'
import { fromFHIR, toFHIR } from '../../../../lib/Proxy/medications'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { capitalize } from '../../../igao/utils'
import * as Card from '../../../shared/card'
import CrudTable from '../../../shared/CrudTable'
import { getEntries, setListEmpty } from '../../../shared/CrudTable/actions'
import FriendlyDate from '../../../shared/Date/FriendlyDate'
import ConfirmationDeleteDialog from '../../../shared/DeleteDialog/ConfirmationDeleteDialog'
import { Checkbox, FlatButton } from '../../../shared/NarusMaterial'

import MedicationForm from './MedicationForm'

const FILTERS = {
  active: med => med.status === 'active',

  inactive: med => med.status !== 'active'
}

const getAmount = amount => {
  if (!amount) {
    return
  }
  return Amounts.find(a => a.code === amount).label
}

const getUnit = unit => {
  if (!unit) {
    return
  }
  return Units.find(dictUnit => dictUnit.value.code === unit.code).label
}

const getRoute = route => {
  if (!route) {
    return
  }
  return Routes.find(dictRoute => dictRoute.value.code === route.code)
    .label
}

const getFrequency = freq => {
  if (!freq) {
    return
  }
  return capitalize(Frequencies.find(
    dictFreq => dictFreq.code === freq
  )?.label ?? '')
}

const medicationStructure = [
  {
    title: 'Medication',
    displayFn: v => _.get(v, 'coding.display')
  },
  {
    title: 'Start Date',
    displayFn: v => <FriendlyDate date={_.get(v, 'start')} />,
    sortBy: 'start'
  },
  {
    title: 'End Date',
    displayFn: v => <FriendlyDate date={_.get(v, 'stop')} />,
    sortBy: 'stop'
  },
  {
    title: 'Amt',
    displayFn: v => getAmount(_.get(v, 'dose'))
  },
  {
    title: 'Unit',
    displayFn: v => getUnit(_.get(v, 'unit'))
  },
  {
    title: 'Route',
    displayFn: v => getRoute(_.get(v, 'route'))
  },
  {
    title: 'Frequency',
    displayFn: v => getFrequency(_.get(v, 'frequency'))
  },
  {
    title: 'Schedule',
    displayFn: v => {
      const prn = _.get(v, 'prn')
      const atc = _.get(v, 'atc')
      const arr = [ prn ? 'PRN' : null, atc ? 'ATC' : null ]
      const value = _.compact(arr)
      return _.size(value) > 1 ? arr.join(' & ') : _.head(value)
    }
  },
  {
    title: 'Indication',
    displayFn: v => _.capitalize(v.indication)
  },
  {
    title: 'Presrcriber',
    displayFn: v => _.get(v, 'prescriber.display')
  }
]

const medicationStructurePatientBanner = [
  {
    title: 'Medication',
    displayFn: v => v.coding.display
  },
  {
    title: 'Dose & Freq',
    displayFn: v => (v.frequency ? `${v.dose} & ${v.frequency}` : v.dose)
  }
]

export function Medications ({ isPatientBanner, callbackFn, patientId: patientIdProps }) {
  const [ filter, setFilter ] = useState('active')
  const dispatch = useAppDispatch()

  const { isListEmpty, entries, loading, patientId: patientIdState, clientName } = useAppSelector((state) => {
    const medications = state.crudTable?.medications
    return {
      isListEmpty: medications?.additionalData?.empty ?? false,
      entries: medications?.entries ?? [],
      loading: medications?.getNewEntries ?? true,
      patientId: state.globalVars?.params?.id,
      clientName: state.globalVars?.params?.clientName
    }
  })

  const patientId = patientIdProps ?? patientIdState

  const medicationsUrls = useMemo(() => ({
    get: () => `/patients/${patientId}/lists/medications`,
    create: () => `/patients/${patientId}/lists/medications`,
    update: entry =>
      `/patients/${patientId}/lists/medications/${entry.id}`,
    delete: entry =>
      `/patients/${patientId}/lists/medications/${entry.id}?code=${entry.coding.code}`
  }), [ patientId ])

  const mapFns = useMemo(() => ({
    toForm: fromFHIR,
    toFhir: entry => toFHIR(entry, patientId),
    toFhirDelete: (entry, formData) => {
      const fhirResource = toFHIR(entry, patientId)
      fhirResource.status = formData.reason

      return fhirResource
    }
  }), [ patientId ])

  useEffect(() => {
    if (patientId) {
      dispatch(
        getEntries('medication', medicationsUrls, mapFns, false)
      )
    }
  }, [ dispatch, mapFns, medicationsUrls, patientId ])

  return (
    <Card.Card expandable>
      {!isPatientBanner && (
        <Card.Header>
          <h2>Medications</h2>
          <Checkbox
            label='Patient reports no medications'
            value={isListEmpty}
            disabled={loading || entries.length > 0}
            onChange={val => {
              dispatch(
                setListEmpty(
                  'medication',
                  `/patients/${patientId}/lists/medications`,
                  clientName,
                  val,
                  medicationsUrls,
                  mapFns
                )
              )
            }}
          />
        </Card.Header>
      )}

      <Card.ExpandableWrapper style={{ padding: '1rem' }}>
        <div style={{ padding: '0.5rem 0' }}>
          {!isPatientBanner && (
            <FlatButton
              label='Active'
              grey={filter !== 'active'}
              onClick={() => setFilter('active')}
            />
          )}

          {!isPatientBanner && (
            <FlatButton
              label='Inactive'
              grey={filter !== 'inactive'}
              onClick={() => setFilter('inactive')}
            />
          )}
        </div>

        <CrudTable
          urlFns={medicationsUrls}
          entryType='medication'
          entrySet={patientId}
          tableDefinition={
            isPatientBanner
              ? medicationStructurePatientBanner
              : medicationStructure
          }
          mapFns={mapFns}
          filterFn={FILTERS[filter]}
          form={MedicationForm}
          deleteDialog={ConfirmationDeleteDialog}
          additionalDeleteDialogProps={{ prompt: 'Really delete?' }}
          additionalFormProps={{ patientId }}
          disabled={isListEmpty}
          readOnly={isPatientBanner}
          noDelete={false}
          callbackFn={() => {
            callbackFn?.()
          }}
        />
      </Card.ExpandableWrapper>
    </Card.Card>
  )
}
